import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { getDeepDive, getDeepDiveReview } from '../../api/client';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthKeyLabel = (key) => { const [y, m] = String(key).split('-'); return `${MONTHS[+m - 1]} ${y}`; };
// The user's local date — sent to the server, whose UTC clock may already be on tomorrow.
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
// Period keys are plain 'YYYY-MM' strings, one calendar month apart. This math needs no
// knowledge of the household's day-of-month anchor — that only affects start/end DATES,
// not which key comes before which (the server owns that, via periodBounds).
const priorKey = (key) => {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, m - 2, 1); // m-1 = this month (0-based); one more back = prior month
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const VERDICT_STYLE = {
  gave_room: { icon: '✅', label: 'Gave room' },
  tighten:   { icon: '⚠️', label: 'Needs tightening' },
  mixed:     { icon: '⚖️', label: 'Mixed' },
};

const MonthlyDeepDive = () => {
  const { COLORS } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const monthFromUrl = /^\d{4}-\d{2}$/.test(searchParams.get('month') || '') ? searchParams.get('month') : null;
  const [currentKey, setCurrentKey]       = useState(null); // the server's "current period" — for the picker
  const currentKeyRef = useRef(null);
  const [monthKey, setMonthKey]           = useState(monthFromUrl); // the period actually being viewed
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [loadError, setLoadError]         = useState(false);
  const [review, setReview]               = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Load the requested (or current) period. When deep-linked via ?month=, also fetch
  // the real current period key once so the picker still covers recent months.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const params = { today: localToday() };
        if (monthFromUrl) params.month = monthFromUrl;
        const needCurrent = monthFromUrl && !currentKeyRef.current;
        const [res, curRes] = await Promise.all([
          getDeepDive(params),
          needCurrent ? getDeepDive({ today: localToday() }) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setData(res.data);
        setMonthKey(res.data.key);
        const nextCurrent = curRes ? curRes.data.key : (!monthFromUrl ? res.data.key : currentKeyRef.current);
        if (nextCurrent) {
          currentKeyRef.current = nextCurrent;
          setCurrentKey(nextCurrent);
        }
      } catch (e) {
        if (!cancelled) setLoadError(true);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [monthFromUrl]);

  // Separate + slower: the numbers above render instantly, the AI review streams in after.
  const loadReview = useCallback(async (month) => {
    setReviewLoading(true);
    setReview(null);
    try {
      const res = await getDeepDiveReview({ month, today: localToday() });
      setReview(res.data.review);
    } catch (e) { /* the numbers still stand without the AI review */ }
    setReviewLoading(false);
  }, []);

  useEffect(() => { if (monthKey) loadReview(monthKey); }, [monthKey, loadReview]);

  const handleMonthChange = (key) => {
    setSearchParams(key ? { month: key } : {});
  };

  // Last 12 periods, current first — generated purely from the anchor key we learned above.
  const periodOptions = useMemo(() => {
    if (!currentKey) return [];
    const keys = [currentKey];
    for (let i = 1; i < 12; i++) keys.push(priorKey(keys[i - 1]));
    return keys;
  }, [currentKey]);

  const inp = { padding: '10px 12px', background: COLORS.inputBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontSize: 14, fontFamily: 'inherit', outline: 'none' };
  const netColor = (n) => (n < 0 ? COLORS.red : COLORS.green);

  // One Planned | Actual | Diff row.
  const Row = ({ label, sub, planned, actual, diff, muted }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr 1fr 1fr', gap: 8, alignItems: 'center', padding: '8px 14px', borderBottom: `1px solid ${COLORS.border}` }}>
      <span style={{ color: muted ? COLORS.textMuted : COLORS.text, fontSize: 13, fontWeight: 500, fontStyle: muted ? 'italic' : 'normal' }}>
        {label}{sub && <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 400 }}> · {sub}</span>}
      </span>
      <span style={{ color: COLORS.textMuted, fontFamily: 'monospace', fontSize: 12.5, textAlign: 'right' }}>{fmt(planned)}</span>
      <span style={{ color: COLORS.text, fontFamily: 'monospace', fontSize: 12.5, textAlign: 'right' }}>{fmt(actual)}</span>
      <span style={{ color: netColor(diff), fontFamily: 'monospace', fontSize: 12.5, fontWeight: 700, textAlign: 'right' }}>{fmt(diff)}</span>
    </div>
  );

  const ColumnHeader = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr 1fr 1fr', gap: 8, padding: '6px 14px', background: COLORS.inputBg, borderBottom: `1px solid ${COLORS.border}` }}>
      <span />
      {['Planned', 'Actual', 'Diff'].map((h) => (
        <span key={h} style={{ color: COLORS.textMuted, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'right' }}>{h}</span>
      ))}
    </div>
  );

  // `sign` flips the diff convention: +1 for income (actual - planned is good),
  // -1 for expenses (planned - actual is good) — see projection.js's retrospective().
  const Section = ({ title, section, sign }) => {
    const totalDiff = sign * (section.actualTotal - section.plannedTotal);
    const empty = section.lines.length === 0 && section.unbudgeted === 0;
    return (
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
        <div style={{ padding: '10px 14px', background: COLORS.inputBg, borderBottom: `1px solid ${COLORS.border}` }}>
          <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</span>
        </div>
        {empty ? (
          <div style={{ padding: '14px', color: COLORS.textMuted, fontSize: 12 }}>Nothing to show for this period.</div>
        ) : (
          <>
            <ColumnHeader />
            {section.lines.map((l) => (
              <Row key={l.id} label={l.name} sub={l.scheduleType || l.type} planned={l.planned} actual={l.actual} diff={l.diff} />
            ))}
            {section.unbudgeted !== 0 && (
              <Row label="Unbudgeted / Other" muted planned={0} actual={section.unbudgeted} diff={sign * section.unbudgeted} />
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr 1fr 1fr', gap: 8, padding: '10px 14px', background: COLORS.inputBg }}>
              <span style={{ color: COLORS.text, fontSize: 12, fontWeight: 800 }}>Total</span>
              <span style={{ color: COLORS.textMuted, fontFamily: 'monospace', fontSize: 12.5, textAlign: 'right', fontWeight: 700 }}>{fmt(section.plannedTotal)}</span>
              <span style={{ color: COLORS.text, fontFamily: 'monospace', fontSize: 12.5, textAlign: 'right', fontWeight: 700 }}>{fmt(section.actualTotal)}</span>
              <span style={{ color: netColor(totalDiff), fontFamily: 'monospace', fontSize: 12.5, fontWeight: 800, textAlign: 'right' }}>{fmt(totalDiff)}</span>
            </div>
            {/* Transparent footnote — money flagged excluded_from_pace (one-time/windfall
                spending) is left out of the totals above by design, but should never look
                like it silently vanished. */}
            {section.excludedTotal !== 0 && (
              <div style={{ padding: '8px 14px', fontSize: 11.5, color: COLORS.textMuted, fontStyle: 'italic', borderTop: `1px solid ${COLORS.border}` }}>
                +{fmt(section.excludedTotal)} excluded — flagged one-time / windfall spending, kept out of these totals on purpose
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '20px 16px', fontFamily: "'DM Sans', sans-serif" }}>
      <h2 style={{ color: COLORS.text, fontSize: 24, fontWeight: 800, margin: '0 0 4px', letterSpacing: -0.5 }}>Monthly Deep Dive</h2>
      <p style={{ color: COLORS.textMuted, fontSize: 14, margin: '0 0 16px' }}>
        Planned vs. actual for one budget period — see if you had room to give, or need to tighten up.
      </p>

      {periodOptions.length > 0 && (
        <select value={monthKey || ''} onChange={(e) => handleMonthChange(e.target.value)} style={{ ...inp, width: '100%', marginBottom: 16 }}>
          {periodOptions.map((k, i) => (
            <option key={k} value={k}>{monthKeyLabel(k)}{i === 0 ? ' (so far)' : ''}</option>
          ))}
        </select>
      )}

      {loading || !data ? (
        <p style={{ color: COLORS.textMuted }}>Loading…</p>
      ) : loadError ? (
        <p style={{ color: COLORS.red }}>Couldn't load this period. Try again in a moment.</p>
      ) : (
        <>
          {/* Verdict banner */}
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '16px 18px', marginBottom: 14 }}>
            <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
              {data.label} · {data.start} – {data.end}
            </div>
            <div style={{ color: netColor(data.netDiff), fontFamily: 'monospace', fontSize: 26, fontWeight: 800, marginBottom: 4 }}>
              {data.netDiff >= 0 ? `+${fmt(data.netDiff)} room to give` : `${fmt(data.netDiff)} — time to tighten`}
            </div>
            <div style={{ color: COLORS.textMuted, fontSize: 12 }}>
              Planned net {fmt(data.plannedNet)} · Actual net {fmt(data.actualNet)}
            </div>
          </div>

          {/* AI Monthly Review */}
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.green})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🪙</div>
              <span style={{ color: COLORS.text, fontWeight: 700, fontSize: 13 }}>Penny's Monthly Review</span>
            </div>
            {reviewLoading ? (
              <div style={{ color: COLORS.textMuted, fontSize: 13 }}>Reviewing your month…</div>
            ) : review ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <span>{VERDICT_STYLE[review.verdict]?.icon || '📊'}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {VERDICT_STYLE[review.verdict]?.label || 'Review'}
                  </span>
                </div>
                <p style={{ color: COLORS.text, fontSize: 14, lineHeight: 1.5, margin: '0 0 12px' }}>{review.headline}</p>
                {[['Wins', review.wins, COLORS.green], ['Leaks', review.leaks, COLORS.red], ['Watchlist', review.watchlist, COLORS.amber]].map(([label, items, color]) => (
                  items && items.length > 0 && (
                    <div key={label} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
                      <ul style={{ margin: 0, paddingLeft: 18, color: COLORS.textMuted, fontSize: 12.5, lineHeight: 1.6 }}>
                        {items.map((it, i) => <li key={i}>{it}</li>)}
                      </ul>
                    </div>
                  )
                ))}
              </>
            ) : (
              <div style={{ color: COLORS.textMuted, fontSize: 13 }}>Review unavailable right now — the numbers above are still accurate.</div>
            )}
          </div>

          <Section title="Income" section={data.income} sign={1} />
          <Section title="Expenses" section={data.expenses} sign={-1} />
        </>
      )}
    </div>
  );
};

export default MonthlyDeepDive;
